// src/pages/Admin/Dashboard/Events/utils/eventsUtils.js

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

export const INITIAL_EVENT_FORM = {
  titre: "",
  description: "",
  start: "",
  end: "",
  type_event: "",
  level_event: "",
  cate_event: "",
  lieu_event: "",
  horaire_event: "",
  photo_url: "",
  agenda_url: "",
  is_active: true,
  can_register: true,
  max_registrations: "",
  imageFile: null,
};

export const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const isDemoUser = (user) => Number(user?.role_id) === 4;

export const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(Number(value || 0));

export const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export const getMediaUrl = (url) => {
  if (!url) return "";
  if (isExternalURL(url)) return url;
  return `${BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

export const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const stripHtml = (html = "") =>
  String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const truncateText = (value = "", limit = 150) => {
  const text = stripHtml(value);

  if (text.length <= limit) return text;

  return `${text.slice(0, limit).trim()}...`;
};

export const getSafeRegistrations = (registrationsJson) => {
  if (Array.isArray(registrationsJson)) return registrationsJson;

  if (typeof registrationsJson === "string") {
    try {
      const parsed = JSON.parse(registrationsJson);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
};

export const getRegistrationsCount = (event) =>
  getSafeRegistrations(event?.registrations_json).length;

export const formatEventDate = (value) => {
  if (!value) return "Date non renseignée";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Date invalide";

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "full",
  }).format(date);
};

export const formatEventDateShort = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

export const formatDateForInput = (value) => {
  if (!value) return "";

  // Si la valeur est déjà au format YYYY-MM-DD
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const timezoneOffset = date.getTimezoneOffset() * 60000;
  const localDate = new Date(date.getTime() - timezoneOffset);

  return localDate.toISOString().slice(0, 10);
};

export const normalizeEventPayload = (formData) => ({
  ...formData,
  titre: String(formData.titre || "").trim(),
  description: String(formData.description || "").trim(),
  type_event: String(formData.type_event || "").trim(),
  level_event: String(formData.level_event || "").trim(),
  cate_event: String(formData.cate_event || "").trim(),
  lieu_event: String(formData.lieu_event || "").trim(),
  horaire_event: String(formData.horaire_event || "").trim(),
  photo_url: String(formData.photo_url || "").trim(),
  agenda_url: String(formData.agenda_url || "").trim(),
  start: formData.start || null,
  end: formData.end || null,
  is_active: Boolean(formData.is_active),
  can_register: Boolean(formData.can_register),
  max_registrations:
    formData.max_registrations === "" ||
    formData.max_registrations === null ||
    formData.max_registrations === undefined
      ? null
      : Number(formData.max_registrations),
});

export const validateEventForm = (formData) => {
  if (!String(formData.titre || "").trim()) {
    return "Le titre de l'événement est obligatoire.";
  }

  if (formData.start && formData.end) {
    const startDate = new Date(formData.start);
    const endDate = new Date(formData.end);

    if (
      !Number.isNaN(startDate.getTime()) &&
      !Number.isNaN(endDate.getTime()) &&
      endDate < startDate
    ) {
      return "La date de fin ne peut pas être avant la date de début.";
    }
  }

  if (
    formData.max_registrations !== "" &&
    formData.max_registrations !== null &&
    formData.max_registrations !== undefined &&
    Number(formData.max_registrations) < 0
  ) {
    return "Le nombre maximum d'inscriptions doit être positif.";
  }

  return null;
};

export const buildInitialFormFromEvent = (event) => {
  if (!event) return INITIAL_EVENT_FORM;

  return {
    ...INITIAL_EVENT_FORM,
    ...event,
    start: formatDateForInput(event.start),
    end: formatDateForInput(event.end),
    max_registrations: event.max_registrations ?? "",
    imageFile: null,
  };
};

export const filterAndSortEvents = (events = [], filters = {}) => {
  const query = normalizeText(filters.query);

  let result = [...events];

  if (query) {
    result = result.filter((event) => {
      const haystack = normalizeText(
        [
          event.titre,
          event.description,
          event.type_event,
          event.level_event,
          event.cate_event,
          event.lieu_event,
          event.horaire_event,
        ]
          .filter(Boolean)
          .join(" "),
      );

      return haystack.includes(query);
    });
  }

  if (filters.status === "active") {
    result = result.filter((event) => Boolean(event.is_active));
  }

  if (filters.status === "inactive") {
    result = result.filter((event) => !event.is_active);
  }

  if (filters.registration === "open") {
    result = result.filter((event) => Boolean(event.can_register));
  }

  if (filters.registration === "closed") {
    result = result.filter((event) => !event.can_register);
  }

  result.sort((a, b) => {
    const dateA = a.start ? new Date(a.start).getTime() : 0;
    const dateB = b.start ? new Date(b.start).getTime() : 0;

    if (filters.sortOrder === "start_desc") {
      return dateB - dateA;
    }

    if (filters.sortOrder === "title_asc") {
      return normalizeText(a.titre).localeCompare(normalizeText(b.titre));
    }

    if (filters.sortOrder === "title_desc") {
      return normalizeText(b.titre).localeCompare(normalizeText(a.titre));
    }

    return dateA - dateB;
  });

  return result;
};

export const buildEventsStats = (events = [], filteredEvents = []) => {
  const active = events.filter((event) => Boolean(event.is_active)).length;
  const inactive = events.filter((event) => !event.is_active).length;
  const registrationsOpen = events.filter((event) =>
    Boolean(event.can_register),
  ).length;

  const withImage = events.filter((event) => Boolean(event.photo_url)).length;

  const totalRegistrations = events.reduce(
    (total, event) => total + getRegistrationsCount(event),
    0,
  );

  return {
    total: events.length,
    filtered: filteredEvents.length,
    active,
    inactive,
    registrationsOpen,
    withImage,
    totalRegistrations,
  };
};

export const formatRegistrationDate = (value) => {
  if (!value) return "Date non renseignée";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Date invalide";

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};
